import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, Stack, MenuItem, Select, Alert, Snackbar, CircularProgress, Link, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
} from '@mui/material';
import {
  getTenants, setTenantStatus, impersonateTenant,
  requestImpersonation, getMyImpersonationRequests,
} from '../../api/saasApi';
import { useMe } from '../../context/MeContext';
import { useAuth } from '../../context/AuthContext';

const STATUSES = ['Trialing', 'Active', 'PastDue', 'ReadOnly', 'Suspended'];
const color = (s) => ({ Active: 'success', Trialing: 'info', PastDue: 'warning', ReadOnly: 'error', Suspended: 'error' }[s] || 'default');

const Tenants = () => {
  const { isPlatformAdmin } = useMe();
  const { login } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [reqByTenant, setReqByTenant] = useState({}); // tenantId -> latest request
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');
  // Tenant awaiting a reason in the request-access dialog (null = closed).
  const [requestTarget, setRequestTarget] = useState(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    try {
      const [tRes, rRes] = await Promise.all([getTenants(), getMyImpersonationRequests()]);
      setTenants(tRes.data?.data || []);
      // API returns newest first; keep the first seen per tenant.
      const map = {};
      (rRes.data?.data || []).forEach((r) => { if (!map[r.tenantId]) map[r.tenantId] = r; });
      setReqByTenant(map);
    } catch (e) { setErr('Failed to load tenants.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (tenantId, status) => {
    await setTenantStatus(tenantId, status);
    setToast('Status updated.');
    load();
  };

  // A grant is usable when Approved and not past its window.
  const grantState = (t) => {
    const r = reqByTenant[t.id];
    if (!r) return { kind: 'none' };
    if (r.status === 'Pending') return { kind: 'pending' };
    if (r.status === 'Approved') {
      const ok = !r.approvalExpiresAt || new Date(r.approvalExpiresAt).getTime() > Date.now();
      return ok ? { kind: 'approved', req: r } : { kind: 'expired' };
    }
    return { kind: r.status.toLowerCase() }; // denied | used | expired
  };

  const openRequest = (t) => { setReason(''); setRequestTarget(t); };
  const closeRequest = () => { if (busy !== requestTarget?.id) setRequestTarget(null); };

  // Submitted only from the dialog's "Request access" button; Cancel sends nothing.
  const requestAccess = async () => {
    const t = requestTarget;
    if (!t) return;
    setBusy(t.id); setErr('');
    try {
      const res = await requestImpersonation(t.id, reason.trim());
      if (res.data?.succeeded === false) { setErr(res.data.messages?.[0] || 'Could not request access.'); return; }
      setToast(res.data?.messages?.[0] || 'Access requested — a clinic admin must approve it.');
      setRequestTarget(null);
      await load();
    } catch (e) { setErr(e?.response?.data?.messages?.[0] || 'Could not request access.'); }
    finally { setBusy(null); }
  };

  const enterClinic = async (t) => {
    setBusy(t.id); setErr('');
    try {
      const res = await impersonateTenant(t.id);
      if (res.data?.succeeded === false) { setErr(res.data.messages?.[0] || 'Could not start impersonation.'); await load(); return; }
      const token = res.data?.data;
      if (token) {
        login(token, { impersonating: true, tenantId: t.id });
        setToast('Now impersonating ' + t.name);
        window.location.href = '/patient';
      }
    } catch (e) { setErr(e?.response?.data?.messages?.[0] || 'Could not start impersonation.'); await load(); }
    finally { setBusy(null); }
  };

  const ActionButton = ({ t }) => {
    const g = grantState(t);
    if (g.kind === 'approved') {
      return <Button size="small" variant="contained" color="secondary"
        disabled={busy === t.id} onClick={() => enterClinic(t)}>Enter clinic</Button>;
    }
    if (g.kind === 'pending') {
      return <Tooltip title="Waiting for a clinic admin to approve"><span>
        <Button size="small" variant="outlined" disabled>Awaiting approval</Button>
      </span></Tooltip>;
    }
    const label = g.kind === 'none' ? 'Request access' : 'Request access again';
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant="outlined" disabled={busy === t.id}
          onClick={() => openRequest(t)}>{label}</Button>
        {['denied', 'used', 'expired'].includes(g.kind) &&
          <Typography variant="caption" color="text.secondary">({g.kind})</Typography>}
      </Stack>
    );
  };

  if (!isPlatformAdmin) {
    return <Container sx={{ py: 4 }}><Alert severity="error">Platform admins only.</Alert></Container>;
  }
  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Tenants (Platform Admin)</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Entering a clinic requires that clinic&apos;s admin to approve your request first. Approved access lasts 60 minutes and is audit-logged.
      </Typography>
      {err && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Access</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.id}</TableCell>
                <TableCell>
                  <Link component={RouterLink} to={`/platform/tenants/${t.id}`} underline="hover" fontWeight={600}>
                    {t.name}
                  </Link>
                </TableCell>
                <TableCell>{t.slug}</TableCell>
                <TableCell><Chip size="small" label={t.status} color={color(t.status)} /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Select size="small" value={t.status} onChange={(e) => changeStatus(t.id, e.target.value)}>
                      {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                    <ActionButton t={t} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Dialog open={!!requestTarget} onClose={closeRequest} maxWidth="xs" fullWidth>
        <DialogTitle>Request access to {requestTarget?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            A clinic admin must approve this. Approved access lasts 60 minutes and is audit-logged.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            placeholder="e.g. Helping with a report issue raised by the clinic"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequest} disabled={busy === requestTarget?.id}>Cancel</Button>
          <Button variant="contained" onClick={requestAccess} disabled={busy === requestTarget?.id}>
            {busy === requestTarget?.id ? 'Requesting…' : 'Request access'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
};

export default Tenants;
